// ─── CTA dropdown — single (0-1) or multiple (1-5) ─────────────────────────

import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { InlineIssue } from '../../ad-details/shared';
import type { FieldIssue } from './types';

interface SearchCTADropdownProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: string[];
  min: number;
  max: number;
  fieldIssues?: Record<string, FieldIssue>;
  showErrors?: boolean;
  showWarnings?: boolean;
  allowCustom?: boolean;
  customCharLimit?: number;
}

export function SearchCTADropdown({ values, onChange, options, min, max, fieldIssues, showErrors = false, showWarnings = false, allowCustom = false, customCharLimit = 10 }: SearchCTADropdownProps) {
  const isSingle = max <= 1;
  const [customModes, setCustomModes] = useState<Record<number, boolean>>(() => {
    if (!allowCustom) return {};
    const modes: Record<number, boolean> = {};
    values.forEach((v, i) => {
      if (v && !options.includes(v)) modes[i] = true;
    });
    return modes;
  });

  function isCustom(index: number) {
    return allowCustom && !!customModes[index];
  }

  function toggleMode(index: number) {
    const next = { ...customModes };
    next[index] = !next[index];
    setCustomModes(next);
    const updated = [...values];
    updated[index] = '';
    onChange(updated);
  }

  function updateAt(index: number, value: string) {
    const next = [...values];
    next[index] = value;
    onChange(next);
  }

  function addCta() {
    if (values.length >= max) return;
    onChange([...values, '']);
  }

  function removeAt(index: number) {
    if (values.length <= min) return;
    const nextCustom = { ...customModes };
    delete nextCustom[index];
    const reindexed: Record<number, boolean> = {};
    Object.entries(nextCustom).forEach(([k, v]) => {
      const num = Number(k);
      reindexed[num > index ? num - 1 : num] = v;
    });
    setCustomModes(reindexed);
    onChange(values.filter((_, i) => i !== index));
  }

  const sectionIssue = fieldIssues?.['cta'];
  const hasSectionError = !!(sectionIssue?.errors.length && showErrors);
  const hasSectionWarning = !!(sectionIssue?.warnings.length && showWarnings);

  function renderModeToggle(index: number) {
    if (!allowCustom) return null;
    const custom = isCustom(index);
    return (
      <label className="flex-shrink-0 flex items-center gap-1.5 h-[38px] px-2 rounded-lg border border-slate-200 bg-white cursor-pointer select-none">
        <span className="text-[10px] font-medium text-slate-400 whitespace-nowrap">Custom</span>
        <Switch checked={custom} onCheckedChange={() => toggleMode(index)} />
      </label>
    );
  }

  function renderCtaInput(index: number, value: string, borderClass: string) {
    if (isCustom(index)) {
      return (
        <div className="flex-1 min-w-0">
          <div className="relative">
            <input
              type="text"
              value={value}
              onChange={e => {
                if (e.target.value.length <= customCharLimit) updateAt(index, e.target.value);
              }}
              placeholder="e.g. Discover"
              maxLength={customCharLimit}
              className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0014cc]/10 focus:border-[#0014cc]/50 transition-all ${value ? 'text-slate-900' : 'text-slate-400'} ${borderClass || 'border-slate-300'}`}
            />
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 tabular-nums pointer-events-none">
              {value.length}/{customCharLimit}
            </span>
          </div>
        </div>
      );
    }
    return (
      <select
        value={value}
        onChange={e => updateAt(index, e.target.value)}
        className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0014cc]/10 focus:border-[#0014cc]/50 transition-all cursor-pointer ${value ? 'text-slate-900' : 'text-slate-400'} ${borderClass || 'border-slate-300'}`}
      >
        {min === 0 && isSingle && <option value="">No CTA</option>}
        {(min > 0 || !isSingle) && <option value="">Select CTA...</option>}
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }

  if (isSingle) {
    const issue = fieldIssues?.['cta_0'];
    const borderClass =
      !!(issue?.errors.length) && showErrors
        ? 'border-red-400 focus:border-red-400 focus:ring-red-100'
        : '';
    const sectionBorderColor = hasSectionError ? 'border-red-300' : hasSectionWarning ? 'border-amber-300' : 'border-slate-200';

    return (
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Call To Action</p>
          {min === 0 && <span className="text-[10px] text-slate-300">Optional</span>}
        </div>
        <div className={`border-l-2 ${sectionBorderColor} pl-3 py-1`}>
          <div className="flex items-center gap-2">
            {renderCtaInput(0, values[0] ?? '', borderClass)}
            {renderModeToggle(0)}
          </div>
          <div className="flex items-start justify-between mt-1">
            <div />
            <InlineIssue issue={issue} showErrors={showErrors} showWarnings={showWarnings} inline />
          </div>
        </div>
        {hasSectionError && sectionIssue!.errors.map((msg, i) => (
          <p key={`e${i}`} className="text-xs text-red-600 mt-1">✕ {msg}</p>
        ))}
      </div>
    );
  }

  const sectionBorderColor = hasSectionError ? 'border-red-300' : hasSectionWarning ? 'border-amber-300' : 'border-slate-200';

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-baseline gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Call To Action</p>
          <span className="text-[10px] text-slate-300 tabular-nums">({min}–{max})</span>
        </div>
        <span className="text-[10px] text-slate-300 tabular-nums">{values.length}/{max}</span>
      </div>

      <div className={`border-l-2 ${sectionBorderColor} pl-3 py-1 space-y-2`}>
        {values.map((value, index) => {
          const issue = fieldIssues?.[`cta_${index}`];
          const borderClass =
            !!(issue?.errors.length) && showErrors
              ? 'border-red-400 focus:border-red-400 focus:ring-red-100'
              : '';

          return (
            <div key={index}>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  {renderCtaInput(index, value, borderClass)}
                </div>
                {renderModeToggle(index)}
                <button
                  type="button"
                  onClick={() => removeAt(index)}
                  disabled={values.length <= min}
                  className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-20 disabled:hover:text-slate-400 disabled:hover:bg-transparent flex-shrink-0"
                  title="Remove"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-start justify-between mt-1">
                {values.length < max && index === values.length - 1 ? (
                  <button type="button" onClick={addCta} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0014cc] transition-colors">
                    <Plus className="w-3.5 h-3.5" />
                    Add CTA
                  </button>
                ) : <div />}
                <InlineIssue issue={issue} showErrors={showErrors} showWarnings={showWarnings} inline />
              </div>
            </div>
          );
        })}
      </div>

      {hasSectionError && sectionIssue!.errors.map((msg, i) => (
        <p key={`e${i}`} className="text-xs text-red-600 mt-1">✕ {msg}</p>
      ))}
    </div>
  );
}
